import { useEffect } from "react";
import { useLocation, Link } from "wouter";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Users,
  CreditCard,
  Activity,
  Trophy,
  Tag,
  HandCoins,
  BookOpen,
  GraduationCap,
  Calendar,
  Mail,
  Upload,
  FlaskConical,
  ArrowLeft,
  FileCheck,
  Lightbulb,
  ListChecks,
  MessageSquare,
  Inbox,
  CloudDownload,
  Plug,
  ScrollText,
  Contact,
  Megaphone,
  TrendingDown,
  Star,
  ThumbsDown,
  Flag,
  KeyRound,
  Ticket,
  DollarSign,
  Video,
  Compass,
  Sparkles,
  BrainCircuit,
  Send,
  ClipboardList,
  Target,
} from "lucide-react";
import logoImage from "@assets/EduFocal-Circle Only (1)_1759251009690.png";
import { useAdminQuickAccess, VISIT_COUNTS_KEY } from "@/lib/adminQuickAccess";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const sidebarGroups = [
  {
    label: "Overview",
    items: [
      { title: "Dashboard", href: "/admin", icon: LayoutDashboard },
    ],
  },
  {
    label: "Questions",
    items: [
      { title: "Question Analytics", href: "/admin/question-analytics", icon: BarChart3 },
      { title: "Auto Generation", href: "/admin/question-generation", icon: FlaskConical },
      { title: "Quizzative Questions", href: "/admin/quizzative-questions", icon: ListChecks },
      { title: "Question Bank Export", href: "/admin/quizzative-export", icon: CloudDownload },
      { title: "Integration Hub", href: "/admin/quizzative-integration", icon: Plug },
      { title: "API Logs", href: "/admin/quizzative-api-logs", icon: ScrollText },
      { title: "Bulk Export", href: "/admin/quizzative-bulk-export", icon: Upload },
      { title: "Reported Questions", href: "/admin/reported-questions", icon: Flag },
    ],
  },
  {
    label: "Users & Analytics",
    items: [
      { title: "Platform Stats", href: "/admin/stats", icon: BarChart3 },
      { title: "All Users", href: "/admin/users", icon: Users },
      { title: "Sign-in Health", href: "/admin/sign-in", icon: KeyRound },
      { title: "Subscriptions", href: "/admin/subscriptions", icon: CreditCard },
      { title: "Revenue", href: "/admin/revenue", icon: DollarSign },
      { title: "Metrics", href: "/admin/metrics", icon: BarChart3 },
      { title: "Analytics Dashboard", href: "/admin/analytics", icon: BarChart3 },
      { title: "Signup Attribution", href: "/admin/signup-attribution", icon: Compass },
      { title: "Stripe Pricing", href: "/admin/stripe-pricing", icon: CreditCard },
      { title: "Parents", href: "/admin/parents", icon: Contact },
      { title: "Activity Stream", href: "/admin/activity", icon: Activity },
      { title: "Leaderboard", href: "/admin/leaderboard", icon: Trophy },
      { title: "User Messages", href: "/admin/user-messages", icon: MessageSquare },
      { title: "Comms", href: "/admin/comms", icon: Send },
      { title: "XP Decay Monitor", href: "/admin/xp-decay", icon: TrendingDown },
      { title: "PEP Predictions", href: "/admin/pep-predictions", icon: GraduationCap },
      { title: "Cancellation Feedback", href: "/admin/cancellation-feedback", icon: ThumbsDown },
      { title: "User Feedback", href: "/admin/feedback", icon: MessageSquare },
    ],
  },
  {
    label: "Prizes & Marketing",
    items: [
      { title: "Prize Management", href: "/admin/prize-management", icon: Trophy },
      { title: "Discount Codes", href: "/admin/discounts", icon: Tag },
      { title: "Vouchers", href: "/admin/vouchers", icon: Ticket },
      { title: "Referral Payouts", href: "/admin/referrals", icon: HandCoins },
      { title: "Creator Applications", href: "/admin/creator-applications", icon: Users },
      { title: "Creator Videos", href: "/admin/creator-videos", icon: Megaphone },
    ],
  },
  {
    label: "Content",
    items: [
      { title: "Interactive Lessons", href: "/admin/lessons", icon: Sparkles },
      { title: "Performance Tasks", href: "/admin/performance-tasks", icon: ClipboardList },
      { title: "Courses", href: "/admin/courses", icon: GraduationCap },
      { title: "Data Moat", href: "/admin/moat", icon: BrainCircuit },
      { title: "Reading Materials", href: "/admin/reading-materials", icon: BookOpen },
      { title: "Learning Materials", href: "/admin/materials", icon: Lightbulb },
      { title: "Live Classes", href: "/admin/live-classes", icon: Video },
      { title: "Curriculum", href: "/admin/curriculum", icon: GraduationCap },
      { title: "Academic Calendar", href: "/admin/academic-calendar", icon: Calendar },
      { title: "Brevo Mappings", href: "/admin/brevo-mappings", icon: Mail },
    ],
  },
  {
    label: "Communications",
    items: [
      { title: "Acquisition", href: "/admin/acquisition", icon: Target },
      { title: "Knowledge Base", href: "/admin/knowledge-base", icon: BookOpen },
      { title: "Announcements", href: "/admin/announcements", icon: Megaphone },
      { title: "Banners", href: "/admin/banners", icon: Megaphone },
      { title: "WhatsApp Manager", href: "/admin/whatsapp", icon: MessageSquare },
      { title: "WhatsApp Inbox", href: "/admin/whatsapp-inbox", icon: Inbox },
    ],
  },
  {
    label: "Testing",
    items: [
      { title: "Question Sandbox", href: "/admin/sandbox", icon: FileCheck },
      { title: "Dummy Accounts", href: "/admin/dummy-accounts", icon: FlaskConical },
    ],
  },
  {
    label: "Developer",
    items: [
      { title: "API Keys", href: "/admin/api-keys", icon: KeyRound },
      { title: "OpenEMIS Bridge", href: "/admin/openemis", icon: Plug },
    ],
  },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [location] = useLocation();
  const { favourites, toggleFavourite } = useAdminQuickAccess();

  useEffect(() => {
    if (location === "/admin") return;
    try {
      const raw = localStorage.getItem(VISIT_COUNTS_KEY);
      const counts: Record<string, number> = raw ? JSON.parse(raw) : {};
      counts[location] = (counts[location] || 0) + 1;
      localStorage.setItem(VISIT_COUNTS_KEY, JSON.stringify(counts));
    } catch {}
  }, [location]);

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="p-4">
            <Link href="/admin" className="flex items-center gap-2">
              <img src={logoImage} alt="EduFocal Logo" className="h-7 w-auto" />
              <span className="font-semibold text-sm">EduFocal Admin</span>
            </Link>
          </SidebarHeader>
          <SidebarContent>
            {sidebarGroups.map((group) => (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => {
                      const isActive = item.href === "/admin"
                        ? location === "/admin"
                        : location.startsWith(item.href);
                      const isFavourite = favourites.includes(item.href);
                      const showStar = item.href !== "/admin";
                      return (
                        <SidebarMenuItem key={item.href}>
                          <div className="relative group/sitem flex items-center">
                            <SidebarMenuButton asChild isActive={isActive} className={showStar ? "pr-7" : ""}>
                              <Link href={item.href}>
                                <item.icon className="h-4 w-4" />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                            {showStar && (
                              <button
                                className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded z-10 transition-opacity ${isFavourite ? "opacity-100" : "opacity-0 group-hover/sitem:opacity-100"}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleFavourite(item.href);
                                }}
                                title={isFavourite ? "Unpin from dashboard" : "Pin to dashboard"}
                              >
                                <Star
                                  className={`h-3 w-3 transition-colors ${isFavourite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40 hover:text-muted-foreground"}`}
                                />
                              </button>
                            )}
                          </div>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarFooter className="p-4">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/">
                    <ArrowLeft className="h-4 w-4" />
                    <span>Back to App</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center gap-2 p-3 border-b sticky top-0 bg-background z-[100]">
            <SidebarTrigger />
            <div className="ml-auto">
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
